import { faCalendarDays } from '@fortawesome/free-regular-svg-icons'
import { parseISO } from 'date-fns'
import { useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'

import RefereeActionButtons from '@/components/ActionButtons/RefereeActionButtons'
import DetailPill from '@/components/DetailPill'
import Details from '@/components/Details'
import DetailsCard from '@/components/DetailsCard'
import EmployerReferencePanel from '@/components/EntityPanels/EmployerReferencePanel'
import ExpectedError from '@/components/ExpectedError'
import { Header } from '@/components/Header'
import PageContainer from '@/components/PageContainer'
import useEmployerReferee from '@/hooks/useEmployerReferee'
import useUser from '@/hooks/useUser'
import { useSignalREventHandler } from '@/lib/signalR'
import type {
  EventHandler,
  ShouldHandleEvent,
} from '@/lib/signalR/hooks/useSignalREventHandler'
import { EVENT_TYPES } from '@/lib/signalR/hooks/useSignalREventHandler'
import { formatDate } from '@/utils/date'
import { isEmployer } from '@/utils/subjectHelpers'
import { Layout, Container, ColumnContainer, PanelContainer } from '@/views/Applicant'

interface Params {
  employerId: string
}

const DetailPillsContainer = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 170px;
`

const EmployerRefereePage = () => {
  const history = useHistory()

  const { employerId } = useParams<Params>()

  const { customerId } = useUser()

  const { loading, error, application, fetchSingleApplication, employerReferee } =
    useEmployerReferee()

  const onClose = () => history.goBack()

  useEffect(() => {
    if (customerId) {
      fetchSingleApplication(customerId, employerId)
    }
  }, [fetchSingleApplication, employerId, customerId])

  const shouldHandleSignalREvent = useCallback<ShouldHandleEvent>(
    event => application?.subjectId === event.SubjectId,
    [application],
  )

  const handleSignalREvent = useCallback<EventHandler>(
    event => {
      switch (event.EventType) {
        case EVENT_TYPES.ReportGenerated:
        case EVENT_TYPES.SubjectOutcomeOverridden:
          fetchSingleApplication(event.CustomerId, event.SubjectId)
          break
      }
    },
    [fetchSingleApplication],
  )

  useSignalREventHandler({
    shouldHandleEvent: shouldHandleSignalREvent,
    eventHandler: handleSignalREvent,
  })

  const breadcrumbs = [
    {
      label: 'Referees',
    },
    {
      label: 'Employer',
    },
  ]

  const tenancyStartDate =
    application?.assets && application.assets[0].attributes.startDate

  if (error !== undefined) {
    return (
      <ExpectedError
        text={error}
        imagePath="/assets/svg/failed-to-retrieve-applicant.svg"
      />
    )
  }

  return (
    <>
      <Header
        isLoading={loading}
        hasError={error !== undefined}
        name={application?.name?.toLowerCase() ?? ''}
        subjectId={application?.subjectId}
        breadcrumbs={breadcrumbs}
      />
      <PageContainer>
        {loading
          ? 'Loading...'
          : application &&
            (!isEmployer(application.type) ? (
              <>The subject is not an employer referee</>
            ) : (
              <Layout>
                <Details
                  application={application}
                  actionButtons={
                    employerReferee !== undefined && (
                      <RefereeActionButtons
                        refereeType="employer"
                        referee={employerReferee}
                      />
                    )
                  }
                  detailPills={
                    <DetailPillsContainer>
                      {tenancyStartDate && (
                        <DetailPill
                          icon={faCalendarDays}
                          title="Tenancy start date:"
                          detail={formatDate(parseISO(tenancyStartDate))}
                        />
                      )}
                    </DetailPillsContainer>
                  }
                />
                <Container>
                  <ColumnContainer>
                    <PanelContainer>
                      <EmployerReferencePanel application={application} />
                    </PanelContainer>
                  </ColumnContainer>
                  <ColumnContainer>
                    <PanelContainer>
                      <DetailsCard
                        type="Employer"
                        applicationId={application.applicationId}
                        subjectId={application.subjectId}
                        agencyName={application.customerClientName}
                        onClose={onClose}
                        application={application}
                        fetchSingleApplication={fetchSingleApplication}
                        alwaysExpand
                      />
                    </PanelContainer>
                  </ColumnContainer>
                </Container>
              </Layout>
            ))}
      </PageContainer>
    </>
  )
}

export default EmployerRefereePage
